import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { convertToRaw, EditorState } from "draft-js";
import { MediaModel } from "./MediaModel";
import { CheckOutRedirectOption, CheckOutRedirectTypes } from "../enums";
import { autorun } from "mobx";
import { IAPICheckOutAfterPayModel } from "../interfaces/IAPICheckoutModel";

export class CheckOutAfterPayModel extends CoreModel {
  type: keyof typeof CheckOutRedirectOption;
  media?: MediaModel;
  title: string;
  description: string;
  titleEditorState: EditorState;
  descriptionEditorState: EditorState;
  titleEditorRawContent: string;
  descriptionEditorRawContent: string;
  showReceipt: boolean;
  emailReceipt: boolean;
  allowDownload: boolean;
  redirectType: keyof typeof CheckOutRedirectTypes;
  redirectValue: string;
  buttonText: string;
  showButton: boolean;
  errors: InputErrorModel<CheckOutAfterPayModel>[];
  constructor(data?: Partial<CheckOutAfterPayModel>) {
    super();
    this.type =
      (data && data.type) ||
      (Object.keys(CheckOutRedirectOption).find(
        (key) =>
          CheckOutRedirectOption[key as keyof typeof CheckOutRedirectOption] ===
          CheckOutRedirectOption.None
      ) as keyof typeof CheckOutRedirectOption);
    this.media = data && data.media && new MediaModel(data.media);
    this.title = data?.title || "";
    if (!data) {
      this.title = "Thank you for your purchase.";
    }

    this.description = data?.description || "";
    this.titleEditorState =
      data?.titleEditorState ||
      UtilityMethods.getEditorStateFromPlainText(this.title);
    this.descriptionEditorState =
      data?.descriptionEditorState || EditorState.createEmpty();
    this.titleEditorRawContent =
      data?.titleEditorRawContent || "Thank you for your purchase.";
    this.descriptionEditorRawContent = data?.descriptionEditorRawContent || "";
    this.errors = data?.errors || [];
    this.showReceipt = data?.showReceipt || false;
    this.emailReceipt = data?.emailReceipt || false;
    this.allowDownload = data?.allowDownload || false;
    this.redirectType =
      data?.redirectType ||
      (Object.keys(CheckOutRedirectTypes).find(
        (key) =>
          CheckOutRedirectTypes[key as keyof typeof CheckOutRedirectTypes] ===
          CheckOutRedirectTypes.Website
      ) as keyof typeof CheckOutRedirectTypes);
    this.redirectValue = data?.redirectValue || "";
    this.buttonText = data?.buttonText ?? "Click for More Info";
    this.showButton = data?.showButton || false;
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof CheckOutAfterPayModel) {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  validateRedirectTypes(newErrors: InputErrorModel<CheckOutAfterPayModel>[]) {
    switch (CheckOutRedirectTypes[this.redirectType]) {
      case CheckOutRedirectTypes.Website:
        CoreInputModelUtil.validateWebsite(
          "redirectValue",
          this.redirectValue,
          newErrors
        );
        break;
      case CheckOutRedirectTypes.ClickToEmail:
        CoreInputModelUtil.validateEmail(
          "redirectValue",
          this.redirectValue,
          newErrors
        );
        break;
      case CheckOutRedirectTypes.ClickToCall:
        CoreInputModelUtil.validatePhoneNumber(
          "redirectValue",
          this.redirectValue,
          newErrors
        );
        break;
      case CheckOutRedirectTypes.ClickToText:
        CoreInputModelUtil.validateTextField(
          this.redirectValue,
          "redirectValue",
          newErrors
        );
        break;
    }
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckOutAfterPayModel>[] = [];

    switch (CheckOutRedirectOption[this.type]) {
      case CheckOutRedirectOption.Message:
        const title = this.titleEditorState.getCurrentContent().getPlainText();
        CoreInputModelUtil.validateTextField(
          title,
          "titleEditorState",
          newErrors
        );
        if (this.showButton) {
          this.validateRedirectTypes(newErrors);
          CoreInputModelUtil.validateTextField(
            this.buttonText,
            "buttonText",
            newErrors
          );
        }

        break;
      case CheckOutRedirectOption.Redirect:
        this.validateRedirectTypes(newErrors);
        break;
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPICheckOutAfterPayModel {
    return {
      redirectType: this.type,
      redirect: {
        redirectType: this.redirectType,
        redirectValue: this.redirectValue,
      },
      message: {
        media: this.media?.createAPIModel(),
        title: this.title,
        description: this.description,
        titleEditorRawContent: JSON.stringify(
          convertToRaw(this.titleEditorState.getCurrentContent())
        ),
        descriptionEditorRawContent: JSON.stringify(
          convertToRaw(this.descriptionEditorState.getCurrentContent())
        ),
        showReceipt: this.showReceipt,
        allowDownload: this.allowDownload,
        buttonText: this.buttonText,
        showButton: this.showButton,
        emailReceipt: this.emailReceipt,
        redirectType: this.redirectType,
        redirectValue: this.redirectValue,
      },
    };
  }

  static deserializeAPIModel(
    data: IAPICheckOutAfterPayModel
  ): CheckOutAfterPayModel {
    const mappedData: Partial<CheckOutAfterPayModel> = {
      type: data.redirectType,
      media:
        data.message?.media &&
        MediaModel.deserializeAPIModel(data.message.media),
      title: data?.message?.title || "",
      description: data.message?.description || "",
      titleEditorRawContent: data.message?.titleEditorRawContent,
      descriptionEditorRawContent: data.message?.descriptionEditorRawContent,
      showReceipt: data.message?.showReceipt,
      allowDownload: data.message?.allowDownload,
      buttonText: data.message?.buttonText
        ? data.message?.buttonText
        : undefined,
      showButton: data.message?.showButton,
      emailReceipt: data.message?.emailReceipt,
      redirectType: data.redirect?.redirectType,
      redirectValue: data.redirect?.redirectValue,
      titleEditorState: UtilityMethods.parseEditorState(
        data.message?.titleEditorRawContent
      ),
      descriptionEditorState: UtilityMethods.parseEditorState(
        data.message?.descriptionEditorRawContent
      ),
    };

    return new CheckOutAfterPayModel(mappedData);
  }
}
